import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, type ReactNode } from 'react';
import { spring } from '../../tokens';
import { usePalette } from '../../context/command-palette-context';
import { navigateToSearchResult } from '../../lib/navigate-search-result';
import { PaletteInput } from './palette-input';
import { PaletteResults } from './palette-results';
import type { SearchResult } from '../../lib/search-types';

function EmptyHint({ isConfigured }: { isConfigured: boolean }) {
  return (
    <div className="flex flex-col items-center gap-1.5 py-10 text-center">
      <p className="text-sm text-[#5e6070]">Begynn å skrive for å søke</p>
      {isConfigured ? (
        <p className="text-xs text-[#3e404e]">Saker · Hendelser · Dokumenter · Personer</p>
      ) : (
        <p className="text-xs text-[#3e404e]">Søk er ikke konfigurert · viser saker</p>
      )}
    </div>
  );
}

function PaletteShell({ children }: { children: ReactNode }) {
  const shouldReduce = useReducedMotion();
  const motionProps = shouldReduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { scale: 0.96, opacity: 0, y: -6 },
        animate: { scale: 1, opacity: 1, y: 0 },
        exit: { scale: 0.96, opacity: 0, y: -6 },
        transition: spring.snappy,
      };

  return (
    <motion.div
      {...motionProps}
      className="relative mx-auto w-full max-w-xl overflow-hidden rounded-xl border border-white/[0.1] bg-[#1a1b23]"
      style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.4)' }}
    >
      {children}
    </motion.div>
  );
}

export function CommandPalette() {
  const palette = usePalette();
  const navigate = useNavigate();

  const handleSelect = useCallback(
    (result: SearchResult) => {
      palette.close();
      navigateToSearchResult(navigate, result);
    },
    [palette, navigate],
  );

  const handleHover = useCallback((index: number) => palette.setActiveIndex(index), [palette]);

  return createPortal(
    <AnimatePresence>
      {palette.isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[15vh]"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={palette.close}
        >
          <div className="w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <PaletteShell>
              <PaletteInput value={palette.query} onChange={palette.setQuery} />
              {palette.results.length > 0 ? (
                <PaletteResults
                  results={palette.results}
                  activeIndex={palette.activeIndex}
                  onSelect={handleSelect}
                  onHover={handleHover}
                />
              ) : (
                <EmptyHint isConfigured={palette.isConfigured} />
              )}
            </PaletteShell>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
